import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { genererSynthese, useSalleCompte, useSalleQuestions } from '../../../hooks/useSalle';
import { libelleCompteur, PERIODE_MS, TONS } from './libelleCompteur';

/**
 * COMPTEUR DE LA SALLE — dans le bandeau du prompteur, à côté de « relié ».
 *
 * Les questions arrivent par la page `/q` (QR code de la slide 3) pendant
 * toute la conférence. Adrien n'a pas à s'interrompre pour les regarder : le
 * nombre lui suffit, d'un coup d'œil, pour savoir si la salle joue le jeu.
 *
 * DEUX ÉLÉMENTS, ET PAS UN DE PLUS (19/09). Le bandeau du prompteur est déjà
 * plein — chrono, écart, relevé, taille, F1, navigation, état de liaison — et
 * ce bloc y affichait six choses à la fois : « 💬 3 questions · synthèse à
 * jour ♪ ⚠ ⟳ synthèse ☰ ». Il ne reste que :
 *
 *  1. **le nombre, suivi d'une pastille d'état** — et c'est LE LIEN vers la
 *     liste des questions. Le nombre dit si le formulaire vit (il monte
 *     pendant le silence de la slide 3) ; la pastille dit où en est la
 *     synthèse : ✓ elle couvre tout, +3 il manque trois questions, ⚠ le
 *     dernier passage a signalé quelque chose, ♪ la voix est prête. Tout le
 *     reste — le détail en toutes lettres — est dans l'infobulle.
 *  2. **« ⟳ »**, le déclencheur, en icône seule : le bouton en toutes lettres
 *     vit désormais dans le conducteur, à la slide 31, là où le geste se fait.
 *
 * La liste qui s'ouvre au clic est la SEULE dont Adrien dispose pendant les
 * questions-réponses : plus aucun script ne tourne en salle.
 *
 * QUATRE PARTIS PRIS :
 *
 *  1. **Rien ne bat tout seul** (19/09, demande d'Adrien). Le compteur se lit
 *     à l'ouverture du prompteur et d'un clic dessus ; il ne se rafraîchit de
 *     lui-même que pendant une génération, le temps de savoir qu'elle est
 *     finie. ⚠️ CONSÉQUENCE À CONNAÎTRE : c'est ce battement qui empêchait
 *     Render (offre gratuite) de s'endormir pendant la conférence. Le réveil
 *     repose maintenant sur la page `/q`, qui interroge le backend toutes les
 *     30 s depuis chaque téléphone de la salle, et sur le compteur de la
 *     slide 3. Sans public connecté, le backend peut se rendormir : cliquer
 *     le compteur avant de monter sur scène le réveille.
 *  2. **Jamais `AdminAxios`.** Il renvoie vers `/admin` à la première 401 — sur
 *     le prompteur, ce serait perdre le conducteur en pleine conférence pour
 *     un simple compteur. Ici, une 401 affiche « reconnexion » et le prompteur
 *     reste en place. (Le jeton dure 3 h et se renouvelle tout seul : voir
 *     `hooks/useSalle.js`. La 401 ne devrait plus arriver — on la gère quand
 *     même, c'est le genre de chose qui arrive le seul jour où ça compte.)
 *  3. **Une panne ne fait pas disparaître le chiffre.** Le dernier nombre connu
 *     reste affiché, grisé : un compteur qui clignote entre « 12 » et « ? » au
 *     gré du wifi attire l'œil pour rien.
 *  4. **Le compteur ne montre JAMAIS le texte des questions.**
 *     `/api/salle/compte` ne renvoie que des nombres, par construction. Le
 *     texte n'arrive que dans le panneau, qu'on ouvre exprès — le prompteur
 *     est parfois filmé, et l'écran projeté n'est qu'à un raccourci de là.
 *
 * Voir docs/conferences/seminaire-QUESTIONS-SALLE.md.
 * Thème CLAIR (tout le prompteur l'est).
 */

// La fonction pure et ses tons vivent a part : le test de Jest ne peut pas
// charger ce fichier-ci, qui tire axios par `useSalle`. Reexportes ici pour
// qui voudrait les lire depuis le composant.
export { libelleCompteur, PERIODE_MS } from './libelleCompteur';

const CompteurSalle = ({ session: demandee = null }) => {
    /* `null` : AUCUN rafraîchissement automatique (demande du 19/09). Le
       compteur se lit à l'ouverture du prompteur, se relit d'un clic, et se
       suit tout seul le temps d'une génération. */
    const {
        questions, syntheseSur, syntheseAudio, generation,
        relire, erreur, expire, session,
    } = useSalleCompte(null, demandee);

    const [ouvert, setOuvert] = useState(false);
    const [envoi, setEnvoi] = useState(false);
    const [echec, setEchec] = useState(null);
    const [flash, setFlash] = useState(false);
    const precedent = useRef(null);
    const { classement, chargement, relire: relireQuestions } = useSalleQuestions({
        actif: ouvert, session: demandee,
    });

    const connu = questions != null;
    const enCours = generation?.en_cours === true;
    const alerte = generation?.alerte || null;
    const etat = expire ? 'expire' : erreur ? 'panne' : connu ? 'ok' : 'attente';

    // Une question de plus : le chiffre s'allume une seconde.
    useEffect(() => {
        if (questions == null) return undefined;
        if (precedent.current != null && questions > precedent.current) {
            setFlash(true);
            const t = setTimeout(() => setFlash(false), 1500);
            precedent.current = questions;
            return () => clearTimeout(t);
        }
        precedent.current = questions;
        return undefined;
    }, [questions]);

    /* LE SEUL RAFRAÎCHISSEMENT AUTOMATIQUE DE CE COMPOSANT, et il ne dure que
       le temps d'une génération : sans lui, on ne saurait jamais que la
       synthèse est prête. Le reste du temps, rien ne bat — c'est une demande
       explicite (19/09). On relit en cliquant le compteur. */
    useEffect(() => {
        if (!enCours) return undefined;
        const t = setInterval(relire, 4000);
        return () => clearInterval(t);
    }, [enCours, relire]);

    /* LA FIN DE GÉNÉRATION EST ANNONCÉE, ELLE N'EST PAS DEVINÉE (21/09).
       Le bloc de questions du conducteur, à la slide 34, doit se remplir tout
       seul quand la synthèse est prête — mais RIEN D'AUTRE ne doit bouger à
       l'écran : Adrien est en train de lire le conducteur, et un prompteur qui
       se recompose sous les yeux lui fait perdre sa ligne.
       D'où cet événement, plutôt qu'un état remonté à un parent commun : seul
       le bloc qui l'écoute se redessine. Même procédé que `salle:synthese`,
       en sens inverse. */
    const etait = useRef(false);
    useEffect(() => {
        if (etait.current && !enCours) window.dispatchEvent(new CustomEvent('salle:pret'));
        etait.current = enCours;
    }, [enCours]);


    /* L'heure du dernier lancement parti de cet onglet, pour la flèche. */
    const dernierLancement = useRef(0);

    /* `parFleche` : le lancement vient de la flèche suivante de la slide 31
       (télécommande ou clavier), pas d'un clic.
       ⚠️ PAS DE `confirm` DANS CE CAS. La télécommande n'a que « suivant » et
       « précédent » : une boîte de dialogue ouverte par elle ne se refermerait
       qu'à la souris, et bloquerait le prompteur entier en attendant. On ne
       demande donc rien — mais on ne relance pas non plus une analyse partie
       d'ici il y a moins de dix minutes : un clic en avance sur le bouton du
       conducteur, ou un aller-retour 32 → 31 → 32, ne doit pas la refaire. */
    const lancer = useCallback(async (parFleche = false) => {
        if (enCours || envoi) return;
        if (parFleche) {
            if (Date.now() - dernierLancement.current < 10 * 60 * 1000) return;
        } else if (syntheseSur != null && !window.confirm(
            // Une synthèse déjà déposée : on ne la refait pas par accident.
            `Une synthèse existe déjà (${syntheseSur} questions). En refaire une ?`)) return;
        dernierLancement.current = Date.now();
        setEnvoi(true);
        setEchec(null);
        try {
            await genererSynthese(session);
            relire();
        } catch (e) {
            setEchec(e?.response?.data?.error || 'échec du lancement');
        } finally {
            setEnvoi(false);
        }
    }, [enCours, envoi, syntheseSur, session, relire]);

    /* Le bouton « ⟳ analyser les questions » posé DANS le conducteur, à la
       slide 31 : il passe par un événement plutôt que par une prop, comme F1
       (voir RenduConducteur). L'état de la génération reste ici, où il
       s'affiche. */
    useEffect(() => {
        const surDemande = (e) => lancer(e?.detail?.parFleche === true);
        window.addEventListener('salle:synthese', surDemande);
        return () => window.removeEventListener('salle:synthese', surDemande);
    }, [lancer]);

    /* LE TEXTE LONG PASSE EN INFOBULLE (19/09). Le bandeau affichait
       « 💬 3 questions · synthèse à jour ♪ ⚠ ⟳ synthèse ☰ » : six éléments
       pour une information qu'on consulte d'un coup d'œil, au milieu d'un
       bandeau déjà plein. Ne reste à l'écran que le nombre et une pastille
       d'état ; le reste se lit au survol, quand on a le temps.
       `libelleCompteur` garde donc son utilité — elle rédige l'infobulle. */
    const { texte, detail } = libelleCompteur(
        connu ? { questions, synthese_sur: syntheseSur } : null, etat);

    const retard = connu && syntheseSur != null ? questions - syntheseSur : null;

    const infobulle = [
        `Questions de la salle — session « ${session} ».`,
        connu ? `${texte}${detail ? ` · ${detail}` : ''}` : null,
        syntheseSur == null ? 'Aucune synthèse : bouton ⟳.' : null,
        syntheseAudio ? 'Voix prête : « suivant » sur la slide 34.' : null,
        syntheseSur != null && !syntheseAudio ? 'Pas de voix : synthèse à lire à voix haute.' : null,
        alerte ? `⚠️ ${alerte}` : null,
        expire ? 'Jeton admin expiré : se reconnecter sur /admin dans un autre onglet.' : null,
        erreur && !expire ? 'Backend injoignable — dernier nombre connu affiché.' : null,
        '',
        'Cliquer : la liste des questions.',
    ].filter((l) => l !== null).join('\n');

    return (
        <>
            {/* LE NOMBRE EST LE LIEN VERS LA LISTE. Un seul geste, sur la
                seule chose qu'on regarde. L'ouverture relit au passage le
                compteur et les questions — rien ne bat plus tout seul. */}
            <button
                type="button"
                onClick={() => { setOuvert((o) => !o); if (!ouvert) { relire(); relireQuestions(); } }}
                className={`flex items-baseline gap-1 text-xs font-semibold tabular-nums rounded px-1.5 py-0.5 transition-colors ${ouvert ? 'bg-sky-100 text-sky-800' : `hover:bg-slate-100 ${TONS[etat === 'ok' ? 'ok' : 'perime']}`
                    } ${flash ? 'bg-amber-100' : ''}`}
                title={infobulle}
            >
                <span aria-hidden="true">💬</span>
                <span>{connu ? questions : '—'}</span>

                {/* La pastille d'état, et rien d'autre : ✓ la synthèse couvre
                    tout ce qui est arrivé, +n il en manque, ⚠ quelque chose
                    s'est mal passé au dernier passage. */}
                {enCours ? (
                    <span className="text-violet-700 animate-pulse" aria-hidden="true">⟳</span>
                ) : alerte ? (
                    <span className="text-amber-600" aria-hidden="true">⚠</span>
                ) : retard === null ? null : retard > 0 ? (
                    <span className="text-amber-600 font-bold">+{retard}</span>
                ) : (
                    <span className="text-emerald-600" aria-hidden="true">✓</span>
                )}
                {syntheseAudio && !enCours && <span className="text-emerald-600" aria-hidden="true">♪</span>}
            </button>

            {/* Le déclencheur. Icône seule : le bouton en toutes lettres vit
                maintenant dans le conducteur, à la slide 31, là où le geste se
                fait vraiment. Celui-ci n'est plus qu'un second accès. */}
            <button
                onClick={() => lancer()}
                disabled={enCours || envoi || !connu || questions === 0}
                className={`w-8 h-8 rounded border text-sm font-bold transition-colors disabled:opacity-40 ${enCours || envoi
                    ? 'border-violet-300 bg-violet-100 text-violet-800 animate-pulse'
                    : 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                title={questions === 0
                    ? 'Aucune question à synthétiser'
                    : enCours
                        ? `Analyse en cours${generation?.depuis != null ? ` (${generation.depuis} s)` : ''}…`
                        : 'Analyser les questions : classement, synthèse et voix (20 à 60 s). Sur la slide 31, le premier appui sur « suivant » la lance aussi.'}
            >
                ⟳
            </button>

            {echec && <span className="text-xs font-semibold text-rose-700">{echec}</span>}

            {ouvert && (
                <PanneauQuestions
                    classement={classement}
                    chargement={chargement}
                    onFermer={() => setOuvert(false)}
                    onRelire={relireQuestions}
                />
            )}
        </>
    );
};

export default CompteurSalle;

/**
 * Les questions sous les yeux d'Adrien, pendant les questions-réponses.
 *
 * Par-dessus le conducteur et non à côté : à ce moment-là, le conducteur n'a
 * plus rien à dire, et les questions sont tout ce qui compte. Une colonne
 * lisible de loin, les groupes dans l'ordre de la synthèse — c'est l'ordre
 * dans lequel Adrien va répondre.
 */
function PanneauQuestions({ classement, chargement, onFermer, onRelire }) {
    /* ⚠️ PORTAIL OBLIGATOIRE, ET LA RAISON EST SUBTILE. Ce panneau est rendu
       depuis le bandeau du prompteur, qui porte `backdrop-blur` — un
       `backdrop-filter` crée un BLOC CONTENEUR pour les descendants
       `position: fixed`. Rendu sur place, `fixed inset-0` se calait donc sur
       la hauteur du bandeau : le panneau tenait dans une bande de 56 px, sans
       fond, et le conducteur se lisait au travers des questions. */
    const groupes = [
        ...(classement?.themes || []).map((t) => ({ titre: t.titre || 'Sans titre', questions: t.questions, ton: 'sky' })),
        { titre: 'Sans thème', questions: classement?.sans_theme || [], ton: 'slate' },
        // Arrivées après la synthèse : elles ne sont pas dans le texte projeté,
        // donc la salle ne les a pas entendues. À prendre au vol.
        { titre: 'Arrivées depuis la synthèse', questions: classement?.depuis || [], ton: 'amber' },
        { titre: 'Écartées', questions: classement?.ecartees || [], ton: 'slate' },
    ].filter((g) => g.questions.length > 0);

    return createPortal(
        // Fond OPAQUE : `bg-white/97` n'existe pas dans l'échelle Tailwind, la
        // classe n'était même pas générée.
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
            <header className="flex-shrink-0 flex items-center gap-4 border-b border-slate-200 px-6 py-3">
                <h2 className="text-lg font-bold text-slate-900">
                    Questions de la salle
                    {classement?.total != null && (
                        <span className="ml-2 text-sm font-normal text-slate-400 tabular-nums">
                            {classement.total} au total
                        </span>
                    )}
                </h2>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={onRelire} className="px-3 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs text-slate-600">
                        ⟲ relire
                    </button>
                    <button onClick={onFermer} className="px-3 h-8 rounded bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold">
                        fermer
                    </button>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-4">
                {chargement && !classement && <p className="text-slate-400">Lecture…</p>}
                {classement && groupes.length === 0 && (
                    <p className="text-slate-500">
                        Aucune question classée. Lance la synthèse (⟳) : c'est elle qui range les questions.
                    </p>
                )}
                <div className="max-w-4xl mx-auto space-y-6">
                    {groupes.map((g) => (
                        <section key={g.titre}>
                            <h3 className={`text-sm font-bold uppercase tracking-wider mb-2 ${g.ton === 'sky' ? 'text-sky-800'
                                : g.ton === 'amber' ? 'text-amber-800' : 'text-slate-400'
                                }`}>
                                {g.titre}
                                <span className="ml-2 font-mono tabular-nums opacity-60">{g.questions.length}</span>
                            </h3>
                            <ul className="space-y-1.5">
                                {g.questions.map((q) => (
                                    <li key={q.n} className="flex gap-3 text-[17px] leading-snug text-slate-800">
                                        <span className="font-mono text-slate-400 tabular-nums shrink-0">{q.n}.</span>
                                        <span>{q.texte}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </div>,
        document.body,
    );
}
